import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import VideoCard from "@/components/feature/VideoCard";
import PlayerDemo from "@/pages/detail/components/PlayerDemo";
import { typeLabels } from "@/mocks/videos";
import { getRelatedVideos, getVideoById } from "@/utils/videoUtils";
import { useFavorites } from "@/hooks/useFavorites";
import { usePageMeta } from "@/hooks/usePageMeta";

export default function Detail() {
  const { id = "" } = useParams();
  const videoId = Number(id);
  const video = getVideoById(videoId);
  const [playing, setPlaying] = useState(false);
  const [shareTip, setShareTip] = useState("");
  const { isFavorite, toggleFavorite } = useFavorites();

  const related = useMemo(
    () => (video ? getRelatedVideos(video, 6) : []),
    [video]
  );

  usePageMeta({
    title: video ? `${video.title}详情播放-西瓜视频免费` : "影片详情-西瓜视频免费",
    description: video
      ? `${video.title}：${video.summary} ${video.director}执导，${video.actors.join("、")}主演，${video.region}${typeLabels[video.type]}，${video.year}年上映。`
      : "西瓜视频免费为您提供影片详情与在线观看入口。",
    keywords: video
      ? `西瓜视频免费,${video.title},${video.title}在线观看,${typeLabels[video.type]},免费视频`
      : "西瓜视频免费,影片详情",
  });

  if (!video) {
    return (
      <div className="mx-auto flex w-full max-w-[1280px] flex-col items-center justify-center px-4 py-24 text-center">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-500">
          <i className="ri-file-unknow-line text-3xl" />
        </span>
        <h1 className="mt-5 font-heading text-xl md:text-2xl font-bold text-foreground-950">
          未找到该影片
        </h1>
        <p className="mt-2 text-sm text-foreground-600">
          影片可能已下架或链接有误，去看看其他精彩内容吧。
        </p>
        <Link
          to="/"
          className="mt-6 inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
        >
          <i className="ri-home-4-line" />
          返回首页
        </Link>
      </div>
    );
  }

  const favorited = isFavorite(video.id);

  const handleShare = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      setShareTip("链接已复制到剪贴板");
    } catch {
      setShareTip("复制失败，请手动复制浏览器地址");
    }
    window.setTimeout(() => setShareTip(""), 2200);
  };

  const infoRows = [
    { label: "导演", value: video.director },
    { label: "主演", value: video.actors.join(" / ") },
    { label: "类型", value: typeLabels[video.type] },
    { label: "地区", value: video.region },
    { label: "年份", value: String(video.year) },
    { label: "片长", value: video.duration },
    { label: "更新日期", value: video.updatedAt },
  ];

  return (
    <div className="mx-auto w-full max-w-[1280px] px-4 md:px-6 py-6 md:py-10">
      <Breadcrumb
        items={[
          { label: `${typeLabels[video.type]}频道`, to: `/category/${video.type}` },
          { label: video.title },
        ]}
      />

      <div className="mt-4 grid grid-cols-1 gap-6 lg:grid-cols-3 lg:gap-8">
        <div className="lg:col-span-2">
          <PlayerDemo video={video} playing={playing} onToggle={() => setPlaying((v) => !v)} />
        </div>

        <div className="rounded-lg border border-background-200 bg-background-100 p-5 md:p-6">
          <div className="flex items-start justify-between gap-3">
            <div>
              <span className="inline-flex items-center gap-1 rounded-md bg-primary-50 px-2 py-0.5 text-xs font-medium text-primary-700">
                <i className="ri-film-line" />
                {typeLabels[video.type]}
              </span>
              <h1 className="mt-2 font-heading text-xl md:text-3xl font-bold text-foreground-950">
                {video.title}
              </h1>
            </div>
            <span className="flex shrink-0 items-center gap-1 rounded-md bg-accent-100 px-2.5 py-1 font-label text-sm font-semibold text-accent-800">
              <i className="ri-star-fill text-xs" />
              {video.rating.toFixed(1)}
            </span>
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            {video.tags.map((tag) => (
              <span key={tag} className="rounded-full bg-secondary-100 px-2.5 py-1 text-xs text-secondary-900">
                {tag}
              </span>
            ))}
          </div>

          <div className="mt-5 flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => toggleFavorite(video.id)}
              className={`inline-flex flex-1 items-center justify-center gap-1.5 whitespace-nowrap rounded-md px-4 py-2.5 text-sm font-medium transition-colors ${
                favorited
                  ? "bg-primary-500 text-background-50 hover:bg-primary-600"
                  : "border border-background-300 bg-background-50 text-foreground-800 hover:border-primary-300 hover:text-primary-600"
              }`}
            >
              <i className={favorited ? "ri-heart-fill" : "ri-heart-line"} />
              {favorited ? "已收藏" : "收藏"}
            </button>
            <button
              type="button"
              onClick={handleShare}
              className="inline-flex flex-1 items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-600"
            >
              <i className="ri-share-line" />
              分享链接
            </button>
          </div>

          {shareTip && (
            <p className="mt-3 flex items-center gap-1.5 rounded-md bg-accent-50 px-3 py-2 text-xs text-accent-800">
              <i className="ri-checkbox-circle-line" />
              {shareTip}
            </p>
          )}

          <dl className="mt-5 space-y-2.5 border-t border-background-200 pt-5">
            {infoRows.map((row) => (
              <div key={row.label} className="flex gap-3 text-sm">
                <dt className="w-16 shrink-0 text-foreground-500">{row.label}</dt>
                <dd className="text-foreground-900">{row.value}</dd>
              </div>
            ))}
          </dl>
        </div>
      </div>

      <section className="mt-8 md:mt-10">
        <h2 className="flex items-center gap-2 font-heading text-lg md:text-2xl font-bold text-foreground-950">
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-accent-100 text-accent-700">
            <i className="ri-book-open-line" />
          </span>
          内容简介
        </h2>
        <p className="mt-4 max-w-4xl text-sm md:text-base leading-relaxed text-foreground-700">
          {video.description}
        </p>
      </section>

      <section className="mt-10 md:mt-14">
        <h2 className="flex items-center gap-2 font-heading text-lg md:text-2xl font-bold text-foreground-950">
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-50 text-primary-600">
            <i className="ri-movie-line" />
          </span>
          相关推荐
        </h2>
        <div className="mt-5 grid grid-cols-2 gap-3 md:gap-5 sm:grid-cols-3 lg:grid-cols-6">
          {related.map((item, index) => (
            <VideoCard key={item.id} video={item} index={index} />
          ))}
        </div>
      </section>
    </div>
  );
}